import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Inbox,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  RefreshCw,
  FileText,
  Mail,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const STATUS_CONFIG = {
  received: { label: "Received", color: "neutral", icon: Clock },
  processing: { label: "Processing", color: "info", icon: RefreshCw },
  parsed: { label: "Parsed", color: "success", icon: CheckCircle },
  needs_review: { label: "Needs Review", color: "warning", icon: AlertTriangle },
  failed: { label: "Failed", color: "error", icon: XCircle },
  dispatched: { label: "Dispatched", color: "success", icon: CheckCircle },
};

const TARGET_SYSTEMS = {
  vinylflow: "VinylFlow",
  localization: "Localization",
  distribution: "Distribution",
  other: "Other",
};

export default function DashboardPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "all",
    target_system: "all",
    search: "",
  });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== "all") {
        params.append("status", filters.status);
      }
      if (filters.target_system && filters.target_system !== "all") {
        params.append("target_system", filters.target_system);
      }

      const response = await fetch(`${API}/inbound/items?${params}`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch items");

      const data = await response.json();
      setItems(data.items);
    } catch (error) {
      toast.error("Failed to load items");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.target_system]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/stats`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch stats");

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchStats();
  }, [fetchItems, fetchStats]);

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.received;
    const Icon = config.icon;

    return (
      <Badge
        variant="outline"
        className={`status-badge-${config.color} flex items-center gap-1`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getSourceIcon = (source) => {
    if (source === "postmark_email") {
      return <Mail className="w-4 h-4 text-muted-foreground" />;
    }
    return <Upload className="w-4 h-4 text-muted-foreground" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  const filteredItems = items.filter((item) => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return (
      item.raw_subject?.toLowerCase().includes(searchLower) ||
      item.raw_from?.toLowerCase().includes(searchLower) ||
      item.id?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Layout
      title="Inbound Inbox"
      actions={
        <Button
          onClick={() => {
            fetchItems();
            fetchStats();
          }}
          variant="outline"
          size="sm"
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      }
    >
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="card-surface">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>

          <Card className="card-surface">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-amber-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                Needs Review
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.needs_review}</div>
            </CardContent>
          </Card>

          <Card className="card-surface">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-600 flex items-center gap-1">
                <XCircle className="w-4 h-4" />
                Failed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.failed}</div>
            </CardContent>
          </Card>

          <Card className="card-surface">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-emerald-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Dispatched
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.dispatched}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="card-surface mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by subject, sender, or ID..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, search: e.target.value }))
                  }
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>

            <Select
              value={filters.status}
              onValueChange={(value) =>
                setFilters((f) => ({ ...f, status: value }))
              }
            >
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="parsed">Parsed</SelectItem>
                <SelectItem value="needs_review">Needs Review</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="dispatched">Dispatched</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.target_system}
              onValueChange={(value) =>
                setFilters((f) => ({ ...f, target_system: value }))
              }
            >
              <SelectTrigger className="w-[180px]" data-testid="system-filter">
                <SelectValue placeholder="All Systems" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Systems</SelectItem>
                <SelectItem value="vinylflow">VinylFlow</SelectItem>
                <SelectItem value="localization">Localization</SelectItem>
                <SelectItem value="distribution">Distribution</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card className="card-surface">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">Type</TableHead>
              <TableHead>Subject / ID</TableHead>
              <TableHead>From</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Received</TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <div className="skeleton-row" />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className="empty-state py-12">
                    <Inbox className="empty-state-icon" />
                    <p className="text-muted-foreground">No items found</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Items will appear here when emails or files are received
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow
                  key={item.id}
                  className="hover:bg-muted/50 cursor-pointer transition-colors"
                  data-testid={`item-row-${item.id}`}
                >
                  <TableCell>{getSourceIcon(item.source)}</TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium truncate max-w-[300px]">
                        {item.raw_subject || item.id}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {item.id}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {item.raw_from || "-"}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {TARGET_SYSTEMS[item.target_system] || item.target_system}
                    </Badge>
                  </TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDate(item.received_at)}
                  </TableCell>
                  <TableCell>
                    <Link to={`/items/${item.id}`}>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`view-item-${item.id}`}
                      >
                        View
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </Layout>
  );
}
